/**
 * Route copy for `src/routes/orchestration` (keys: `routes.orchestration.<file>.<key>`).
 *
 * `fr` is the source; `en` and `ar` are typed against it, so a missing or extra
 * key fails `tsc` (and parity.test.ts). Keys are referenced from the route files
 * through `t()` / `tr()` — edit values freely, never rename a key without its call site.
 *
 * Locale file: en (typed against ./fr). One file per locale so
 * every non-default locale is a separate, lazily-loaded chunk.
 */

import type { Catalog } from './fr';

const en: Catalog = {
  autonomyControl: {
    autonomyLevelName: 'Autonomy level — {{name}}',
    couldNotLoadTheAutonomyLevels: 'Could not load the autonomy levels. Try again later.',
    updatingTheAutonomyLevelFailed: 'Updating the autonomy level failed',
    personaAutonomy: 'Persona autonomy',
    forEachAiAgentChooseHow: 'For each AI agent, choose how far it can act on its own. The level applies to the next tasks given to it (in a team launch or an individual task).',
    loadingAutonomyLevels: 'Loading autonomy levels…',
    noAiAgentAvailable: 'No AI agent available',
    personasWillBeAvailableOnceThe: 'Personas will be available once the AI registry is initialized.',
  },
  format: {
    scheduled: 'scheduled',
    ready: 'ready',
    inProgress: 'in progress',
    blocked: 'blocked',
    awaitingApproval: 'awaiting approval',
    questionPending: 'question pending',
    inThePool: 'in the pool',
    completed: 'completed',
    failed: 'failed',
    cancelled: 'cancelled',
    queued: 'queued',
    teamAtWork: 'team at work',
    paused: 'paused',
    completed2: 'completed',
    cancelled2: 'cancelled',
  },
  orchestrationFlow: {
    requiresHumanApproval: 'Requires human approval',
    noTaskToShowPlanA: 'No task to show — plan a goal to assemble the team.',
  },
  orchestrationPage: {
    unknownAgent: 'Unknown agent',
    couldNotLoadThePersonaCatalog: 'Could not load the persona catalog.',
    thePlanIsEmptyRephraseYour: 'The plan is empty — rephrase your goal.',
    planningFailedErrorStatusTryAgain: 'Planning failed (error {{status}}). Try again later.',
    planningFailedTryAgainLater: 'Planning failed. Try again later.',
    theTeamStartedThePlan: 'The team started the plan.',
    theAiCircuitBreakerIsActive: 'The AI circuit breaker is active for your organization — runs are paused.',
    launchFailedTryAgainLater: 'Launch failed. Try again later.',
    couldNotRetrieveThisRunS: 'Could not retrieve this run\'s progress.',
    aiTeamOrchestration: 'AI team orchestration',
    describeAGoalTheAiAssembles: 'Describe a goal: the AI assembles a team of personas, builds the plan, then runs it — you stay in control of the sensitive steps.',
    aiTeam: 'AI team',
    aiActivity: 'AI activity',
    orchestrationSections: 'Orchestration sections',
    planExecution: 'Plan & execution',
    autonomy: 'Autonomy',
    giveAGoalOrLaunchThe: 'give a goal or launch the AI team',
    objective: 'Objective',
    eGLaunchABackTo: 'E.g. “Launch a back-to-school campaign for our new product” — the AI decides who does what.',
    describeTheGoalToGiveTo: 'Describe the goal to give to the AI team…',
    newObjective: 'New objective',
    scheduling: 'Scheduling…',
    assembleThePlan: 'Assemble the plan',
    theAiIsAssemblingThePlan: 'The AI is assembling the plan…',
    noPlanYet: 'No plan yet',
    describeAGoalAboveThenClick: 'Describe a goal above, then click “Assemble the plan” to see the team and the task graph.',
    assembledTeamLength: 'Assembled team ({{length}})',
    loadingPersonas: 'Loading personas…',
    noPersonaResolvedForThisPlan: 'No persona resolved for this plan (agents not registered in the tenant).',
    task: 'task',
    estimatedCostEstimatedtotalCr: ' · estimated cost ~{{estimatedTotal}} cr',
    balance: ' · balance ∞',
    balanceVCr: ' · balance {{v}} cr',
    launching: 'Launching…',
    launchTheTeam: 'Launch the team',
    teamProgress: 'Team progress',
    doneTotalCompleted: '{{done}}/{{total}} completed',
    ready: 'ready',
    inProgress: 'in progress',
    blocked: 'blocked',
    awaitingApproval: 'awaiting approval',
    questionsPending: 'questions pending',
    completed: 'completed',
    failures: 'failures',
    cancelled: 'cancelled',
    waitingApprovalTaskSAwaitingA: '{{waiting_approval}} task(s) awaiting a human decision.',
    openApprovals: 'Open approvals →',
    needsInputTaskSAreWaiting: '{{needs_input}} task(s) are waiting for your answer to an agent\'s question.',
    answerTheQuestions: 'Answer the questions →',
    blockedTaskSResourceAlreadyLocked: 'blocked task(s) — resource already locked by another task, or insufficient AI credits. Open a blocked task for details.',
    failedTaskSOpenTheTask: 'failed task(s). Open the task to see the error.',
    planGraph: 'Plan graph',
    clickANodeForDetails: 'Click a node for details',
  },
  structuredOutputView: {
    strategy: 'Strategy',
    plan: 'Plan',
    recommendation: 'Recommendation',
    report: 'Report',
    analysis: 'Analysis',
    collectiveDecision: 'Collective decision',
    item: 'Item',
    noIndividualPositionWasRecordedFor: 'No individual position was recorded for this decision.',
    positionsLength: 'Positions ({{length}})',
    chosenOption: 'Chosen option',
    decidedByPersona: 'Decided by {{persona}}',
    theTeamHasNotConvergedOn: 'The team has not converged on an option yet.',
    rawOutputUnstructuredFormat: 'Raw output (unstructured format)',
    noResultYet: 'No result yet.',
    noAdditionalStructuredDetail: 'No additional structured detail.',
  },
  taskDetailModal: {
    couldNotLoadThisTaskS: 'Could not load this task\'s run.',
    theApprovalIsStillPendingIn: 'The approval is still pending in the Approvals inbox.',
    theRequestWasRejectedTheTask: 'The request was rejected — the task is cancelled.',
    insufficientAiCredits: 'Insufficient AI credits.',
    resourceLockedNewAttemptLater: 'Resource locked — new attempt later.',
    taskResumed: 'Task resumed.',
    resumingTheTaskFailed: 'Resuming the task failed.',
    approvalRequired: 'Approval required',
    openApprovals: 'Open approvals',
    resuming: 'Resuming…',
    resumeAfterApproval: 'Resume after approval',
    dependsOn: 'Depends on',
    thisTaskIsWaitingForA: 'This task is waiting for a human decision. Approve it in the Approvals inbox, then click “Resume after approval”.',
    thisTaskHasNotStartedYet: 'This task has not started yet.',
    taskView: 'Task view',
    result: 'Result',
    steps: 'Steps',
    loading: 'Loading…',
    loadingTheGraph: 'Loading the graph…',
  },
};

export default en;
